import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Typography,
  Button,
  Row,
  Col,
  Tag,
  Progress,
  Alert,
  Spin,
  message,
  Modal,
  Descriptions,
  Table,
  Tabs,
} from 'antd';
import {
  ArrowLeftOutlined,
  DollarOutlined,
  CalendarOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  StarOutlined,
  EyeOutlined,
  FileTextOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import apiClient from '../../services/apiClient';
import LandingLayout from './LandingLayout';
import CreditRatingsTable from './CreditRatingsTable';
import ScanScoreChart from '../shared/ScanScoreChart';
import { ThemeContext } from '../../context/ThemeContext';
import { usePortfolioUser } from './Portfolio/usePortfolioUser';
import './InstrumentDetails.css';
import InstrumentChatBot from './InstrumentChatBot';
import CallPutOptionsDetails from './CallPutOptionsDetails';
import { CashFlowView } from './BondCashFlowPage';
import {
  formatRatingTypeLabel,
  getRatingRowKey,
  groupRatingsByType,
  sortRatingTypes,
} from '../../utils/creditRatingUtils';

const { Title, Text, Paragraph } = Typography;

const InstrumentDetails = ({ skipLayout = false }) => {
  const { isin } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const { userId, authHeaders, requireAuth } = usePortfolioUser();
  console.log('InstrumentDetails - ISIN:', isin);
  // Function to get color based on percentage
  const getColor = (percentage) => {
    if (percentage <= 50) return '#ff4d4f'; // Red
    if (percentage <= 70) return '#faad14'; // Orange
    return '#52c41a'; // Green
  };
  const formatCouponRate = (couponRate) => {
    if (couponRate === null || couponRate === undefined || String(couponRate).trim() === '') {
      return 'N/A';
    }
    return `${String(couponRate).replace(/%/g, '').trim()}%`;
  };
  const formatValueOrDash = (value) => {
    if (value === null || value === undefined) return '-';
    const s = String(value).trim();
    return s === '' ? '-' : value;
  };
  const [bondData, setBondData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingCreditNote, setDownloadingCreditNote] = useState(false);

  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [isInWatchlist, setIsInWatchlist] = useState(false);

  const [scanData, setScanData] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);

  const [creditRatings, setCreditRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [activeRatingTypeTab, setActiveRatingTypeTab] = useState(null);

  const ratingsByType = useMemo(() => groupRatingsByType(creditRatings), [creditRatings]);
  const ratingTypes = useMemo(() => sortRatingTypes(Object.keys(ratingsByType)), [ratingsByType]);
  const previewRatings = activeRatingTypeTab ? ratingsByType[activeRatingTypeTab] || [] : [];

  const [termsheetData, setTermsheetData] = useState(null);
  const [termsheetLoading, setTermsheetLoading] = useState(false);
  const [termsheetError, setTermsheetError] = useState(null);
  const [showAllTermsheetDetails, setShowAllTermsheetDetails] = useState(false);

  const [cashFlowData, setCashFlowData] = useState(null);
  const [cashFlowLoading, setCashFlowLoading] = useState(false);
  const [cashFlowError, setCashFlowError] = useState(null);
  const [showAllCashFlowDetails, setShowAllCashFlowDetails] = useState(false);

  const [optionsData, setOptionsData] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [showCallPutDetails, setShowCallPutDetails] = useState(false);

  // Modal states
  const [modalVisible, setModalVisible] = useState({
    fundamentalDetails: false,
    scanScore: false,
    ratings: false,
    documents: false
  });
  const openModal = (section) => {
    setModalVisible(prev => ({ ...prev, [section]: true }));
  };

  const closeModal = (section) => {
    setModalVisible(prev => ({ ...prev, [section]: false }));
  };


  console.log('URL params:', { isin });

  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:8080/api/v1/sb";

  useEffect(() => {
    fetchBondData();
    fetchTermsheetDetails();
    fetchCashFlowData();
    fetchCallPutOptions();
  }, [isin]);



  // Fetch scan data when bond data is available
  useEffect(() => {
    if (bondData && bondData.isin) {
      fetchScanData(bondData.isin);
      fetchCreditRatings(bondData.isin);
      checkWatchlistStatus(bondData.isin);
    }
  }, [bondData]);

  // Fetch credit ratings
  const fetchCreditRatings = async (isinCode) => {
    setRatingsLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/master-isin/${isinCode}/ratings`);
      const result = response.data;

      if ((result.status === 200 || result.statusCode === 200) && result.data) {
        setCreditRatings(result.data);
      } else {
        setCreditRatings([]);
      }
    } catch (error) {
      console.error('Error fetching credit ratings:', error);
      setCreditRatings([]);
    } finally {
      setRatingsLoading(false);
    }
  };

  useEffect(() => {
    if (ratingTypes.length === 0) {
      setActiveRatingTypeTab(null);
      return;
    }

    if (!activeRatingTypeTab || !ratingTypes.includes(activeRatingTypeTab)) {
      setActiveRatingTypeTab(ratingTypes[0]);
    }
  }, [ratingTypes, activeRatingTypeTab]);

  const fetchBondData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch from API using details endpoint
      if (isin) {
        console.log('Fetching from API for ISIN:', isin);

        // Use the details endpoint with the ISIN parameter
        const endpoint = `${baseUrl}/master-isin/details/${isin}`;

        const response = await apiClient.get(endpoint);
        const result = response.data;

        // Handle the API response structure
        if (result.statusCode === 200 && result.data) {
          // Store API response directly in bondData
          setBondData(result.data);
          console.log('ISIN Details API Response:', result);
        } else {
          console.error('Invalid ISIN details API response structure:', result);
          setError("Invalid response structure from API");
          message.error("Invalid response structure from API");
        }
        setLoading(false);
        return;
      }

      // If no ISIN provided, show error
      setError("No ISIN provided");
      message.error("No ISIN provided");
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bond data:', error);
      setError("Error fetching bond data: " + error.message);
      message.error("Error fetching bond data");
      setLoading(false);
    }
  };

  const fetchTermsheetDetails = async () => {
    if (!isin) {
      setTermsheetData(null);
      setTermsheetError("No ISIN provided");
      return;
    }

    setTermsheetLoading(true);
    setTermsheetError(null);
    try {
      const response = await apiClient.get(`${baseUrl}/termsheet/${isin}`, {
        validateStatus: () => true,
      });
      const result = response.data;

      if (result && result.statusCode === 200) {
        setTermsheetData(result.data);
        setShowAllTermsheetDetails(false);
      } else if (result && result.statusCode === 404) {
        setTermsheetData(null);
        setTermsheetError("No termsheet details available");
      } else {
        throw new Error(result?.message || "Failed to fetch termsheet details");
      }
    } catch (error) {
      console.error("Error fetching termsheet details:", error);
      setTermsheetData(null);
      setTermsheetError("Error fetching termsheet details");
    } finally {
      setTermsheetLoading(false);
    }
  };

  const fetchCashFlowData = async () => {
    if (!isin || !isin.trim()) {
      setCashFlowData(null);
      setCashFlowError("No ISIN provided");
      return;
    }
    setCashFlowLoading(true);
    setCashFlowError(null);
    setCashFlowData(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/bond-cash-flow/${isin.trim()}`,
        { validateStatus: () => true }
      );
      const result = response.data;
      if (result && (result.statusCode === 200 || result.status === 200)) {
        // API returns { statusCode, message, data: { isin, cashFlows: [...] } }
        setCashFlowData(result.data);
        setShowAllCashFlowDetails(false);
      } else if (result && (result.statusCode === 404 || result.status === 404)) {
        setCashFlowData(null);
        setCashFlowError(result.message || "Cash flow data not found");
      } else {
        throw new Error(result?.message || "Failed to fetch cash flow");
      }
    } catch (err) {
      console.error("Error fetching cash flow:", err);
      setCashFlowData(null);
      setCashFlowError(
        err.response?.data?.message || err.message || "Error fetching cash flow"
      );
    } finally {
      setCashFlowLoading(false);
    }
  };

  const fetchCallPutOptions = async () => {
    if (!isin || !isin.trim()) {
      setOptionsData(null);
      setOptionsError('No ISIN provided');
      return;
    }
    setOptionsLoading(true);
    setOptionsError(null);
    setOptionsData(null);
    setShowCallPutDetails(false);
    try {
      const response = await apiClient.get(
        `${baseUrl}/master-isin/${isin.trim()}/options`,
        { validateStatus: () => true },
      );
      const result = response.data;
      if (result && (result.statusCode === 200 || result.status === 200)) {
        setOptionsData(result.data);
      } else if (result && (result.statusCode === 404 || result.status === 404)) {
        setOptionsData(null);
        setOptionsError(result.message || 'No embedded options information available.');
      } else {
        throw new Error(result?.message || 'Failed to fetch options data');
      }
    } catch (err) {
      console.error('Error fetching options data:', err);
      setOptionsData(null);
      setOptionsError(
        err.response?.data?.message || err.message || 'Error fetching options data',
      );
    } finally {
      setOptionsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "N/A";
    return `₹${parseFloat(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatTermsheetLabel = (key) => {
    if (!key) return "";
    const spaced = key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/\s+/g, " ")
      .trim();
    const titleCase = spaced.charAt(0).toUpperCase() + spaced.slice(1);
    return titleCase.replace(/\bIsin\b/g, "ISIN");
  };

  const formatTermsheetValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "null") {
      return "N/A";
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "N/A";
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return value;
  };



  // Fetch scan data
  const fetchScanData = async (isinCode) => {
    setScanLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/scan/details/${isinCode}`);
      const result = response.data;

      if (result) {
        setScanData(result);
        console.log('Scan Data API Response:', result);
      } else {
        console.error('Invalid scan data API response structure:', result);
        setScanData(null);
      }
    } catch (error) {
      console.error('Error fetching scan data:', error);
      setScanData(null);
    } finally {
      setScanLoading(false);
    }
  };

  const checkWatchlistStatus = async (isinCode) => {
    try {
      if (!baseUrl || !userId) {
        setIsInWatchlist(false);
        return;
      }
      const response = await apiClient.get(`${baseUrl}/user/watchlist`, {
        headers: authHeaders(),
      });
      const items = response.data?.data || [];
      const exists = items.some((item) => item.isin === isinCode);
      setIsInWatchlist(exists);
    } catch (error) {
      console.error("Error checking watchlist status:", error);
      setIsInWatchlist(false);
    }
  };

  const handleToggleWatchlist = async () => {
    if (!bondData || !bondData.isin) {
      message.error("ISIN not available");
      return;
    }
    if (!baseUrl) {
      message.error("Please login to manage your watchlist");
      return;
    }
    const auth = requireAuth("Please log in to manage your watchlist");
    if (!auth.ok) return;
    try {
      setWatchlistLoading(true);
      if (isInWatchlist) {
        await apiClient.delete(`${baseUrl}/user/watchlist/${bondData.isin}`, {
          headers: authHeaders(),
        });
        message.success("Removed from watchlist");
        setIsInWatchlist(false);
      } else {
        await apiClient.post(
          `${baseUrl}/user/watchlist/${bondData.isin}`,
          {},
          { headers: authHeaders() }
        );
        message.success("Added to watchlist");
        setIsInWatchlist(true);
      }
    } catch (error) {
      console.error("Error updating watchlist:", error);
      message.error("Failed to update watchlist");
    } finally {
      setWatchlistLoading(false);
    }
  };



  // Function to handle PDF download
  const handleDownloadPDF = async () => {
    if (!bondData || !bondData.isin) {
      message.error('ISIN not available for download');
      return;
    }

    if (downloadingPDF) {
      return; // Prevent multiple downloads
    }

    setDownloadingPDF(true);

    try {
      message.loading('Generating PDF report...', 0);

      const response = await apiClient.get(`${baseUrl}/master-isin/${bondData.isin}/report/pdf`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf',
          'Content-Type': 'application/json',
        },
      });

      // Get the filename from the response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = `ISIN_Report_${bondData.isin}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.destroy();
      message.success('PDF report downloaded successfully!');

    } catch (error) {
      message.destroy();
      console.error('Error downloading PDF:', error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Function to handle Credit Note PDF download
  const handleDownloadCreditNote = async () => {
    if (!bondData || !bondData.isin) {
      message.error('ISIN not available for credit note download');
      return;
    }

    if (downloadingCreditNote) {
      return; // Prevent multiple downloads
    }

    setDownloadingCreditNote(true);

    try {
      message.loading('Generating Credit Note PDF...', 0);

      const response = await apiClient.get(`${baseUrl}/master-isin/${bondData.isin}/credit-note/pdf`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf',
          'Content-Type': 'application/json',
        },
        params: {
          isin: bondData.isin
        }
      });

      // Get the filename from the response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Credit_Note_${bondData.isin}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.destroy();
      message.success('Credit Note PDF downloaded successfully!');

    } catch (error) {
      message.destroy();
      console.error('Error downloading Credit Note PDF:', error);
      message.error('Failed to download Credit Note PDF. Please try again.');
    } finally {
      setDownloadingCreditNote(false);
    }
  };

  const termsheetEntries = termsheetData
    ? Object.entries(termsheetData).filter(
      ([key]) => key !== "createdAt" && key !== "updatedAt"
    )
    : [];
  const termsheetPreviewCount = 8;
  const displayedTermsheetEntries = showAllTermsheetDetails
    ? termsheetEntries
    : termsheetEntries.slice(0, termsheetPreviewCount);

  const cashFlowEntries = cashFlowData?.cashFlows || [];
  const cashFlowPreviewCount = 5;
  const displayedCashFlowEntries = showAllCashFlowDetails
    ? cashFlowEntries
    : cashFlowEntries.slice(0, cashFlowPreviewCount);

  const hasCallPutSummary = bondData
    && !(bondData.callOption === 'No' && bondData.putOption === 'No');
  const canExpandCallPutDetails = Boolean(optionsData) && !optionsError;

  const cashFlowColumns = [
    {
      title: "Series Name",
      dataIndex: "seriesName",
      key: "seriesName",
      width: 140,
      render: (text) => <Text strong>{text || "N/A"}</Text>,
    },
    {
      title: "Cash Flow Type",
      dataIndex: "cashFlows",
      key: "cashFlows",
      width: 130,
      render: (text) => text || "N/A",
    },
    {
      title: "Coupon Date",
      dataIndex: "couponDate",
      key: "couponDate",
      width: 110,
      render: (text) => text || "N/A",
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 110,
      render: (text) => text || "N/A",
    },
    {
      title: "Day Count",
      dataIndex: "dayCount",
      key: "dayCount",
      width: 90,
      render: (text) => text ?? "N/A",
    },
    {
      title: "Days in Period",
      dataIndex: "noOfDaysInCouponPeriod",
      key: "noOfDaysInCouponPeriod",
      align: "center",
      width: 110,
      render: (val) => val ?? "N/A",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 120,
      render: (val) => formatCurrency(val),
    },
  ];


  const loadingContent = (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <Spin size="large" />
      <div style={{ marginTop: '20px', color: isDark ? '#ffffff' : '#000000' }}>
        {isin ? 'Loading ISIN details...' : 'Loading bond details...'}
      </div>

    </div>
  );

  if (loading) {
    return skipLayout ? loadingContent : <LandingLayout>{loadingContent}</LandingLayout>;
  }

  const errorContent = (
    <div style={{ padding: '20px' }}>
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" danger onClick={fetchBondData}>
            Retry
          </Button>
        }
      />
      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
        <Button
          type="primary"
          onClick={() => navigate('/isins')}
          icon={<ArrowLeftOutlined />}
        >
          Browse All ISINs
        </Button>
      </div>
    </div>
  );

  if (error) {
    return skipLayout ? errorContent : <LandingLayout>{errorContent}</LandingLayout>;
  }

  const content = (
    <div className={`bond-detail-container isin-detail-page ${isDark ? 'dark' : ''}`}>
      {/* Fintech header: breadcrumb + title + actions */}
      <header className="isin-detail-header">
        <div className="isin-detail-header-inner">
          <nav className="isin-detail-breadcrumb" aria-label="Breadcrumb">
            <button type="button" className="isin-detail-breadcrumb-link" onClick={() => navigate('/isins')}>
              Instruments
            </button>
            <span className="isin-detail-breadcrumb-sep" aria-hidden>›</span>
            <span className="isin-detail-breadcrumb-current">{bondData.isin || 'Details'}</span>
          </nav>
          <div className="isin-detail-title-row">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="isin-detail-btn-back"
            >
              Back
            </Button>
            <h1 className="isin-detail-title">{bondData.companyIssuerName}</h1>
            <div className="isin-detail-actions">
              <Button
                type={isInWatchlist ? "default" : "primary"}
                icon={<StarOutlined />}
                loading={watchlistLoading}
                onClick={handleToggleWatchlist}
                className="isin-detail-btn-watchlist"
              >
                {isInWatchlist ? "Remove from Watchlist" : "Add to Watchlist"}
              </Button>

            </div>
          </div>
        </div>
      </header>

      {/* Key metrics strip + View details */}
      <div className="isin-detail-metrics">
        <div className="isin-detail-metrics-inner">
          <div className="isin-detail-metric">
            <span className="isin-detail-metric-label">Coupon rate</span>
            <span className="isin-detail-metric-value isin-detail-metric-coupon">{formatCouponRate(bondData.couponRate)}</span>
          </div>
          <div className="isin-detail-metric-divider" aria-hidden />
          <div className="isin-detail-metric">
            <span className="isin-detail-metric-label">Maturity</span>
            <span className="isin-detail-metric-value">{bondData.redemption || 'N/A'}</span>
          </div>
          <div className="isin-detail-metric-divider" aria-hidden />
          <div className="isin-detail-metric">
            <span className="isin-detail-metric-label">Issue size</span>
            <span className="isin-detail-metric-value">{bondData.issueSize ? `${bondData.issueSize} Cr` : 'N/A'}</span>
          </div>
          <div className="isin-detail-metric-divider" aria-hidden />
          <div className="isin-detail-metric">
            <span className="isin-detail-metric-label">Mode of issue</span>
            <span className="isin-detail-metric-value">{bondData.modeOfIssue || 'N/A'}</span>
          </div>

          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openModal('fundamentalDetails')}
            className="isin-detail-metrics-view-btn"
          >
            View details
          </Button>
        </div>
      </div>

      <div className="bond-detail-content isin-detail-content">
        <Row gutter={[24, 24]}>
          <Col xs={24}>

            <Row gutter={[24, 24]} className="isin-detail-sections">
              <Col xs={24} lg={12}>
                <Card
                  className="isin-detail-section-card modern-section-card"
                  title={
                    <div className="isin-detail-card-title">
                      <span className="isin-detail-card-title-text">
                        <StarOutlined className="isin-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Scan Score</Title>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openModal('scanScore')}
                      >
                        View Details
                      </Button>
                    </div>
                  }
                  hoverable
                  onClick={() => openModal('scanScore')}
                  style={{ cursor: 'pointer' }}
                >
                  {scanLoading ? (
                    <div className="isin-detail-card-loading">
                      <Spin size="large" />
                    </div>
                  ) : scanData ? (() => {
                    const score = Math.min(100, Math.max(0, Number(scanData.totalWeightedScore) || 0));
                    const getScoreColor = (s) => {
                      if (s <= 40) return { stroke: '#ff4d4f', valueClass: 'score-low' };
                      if (s <= 70) return { stroke: '#fa8c16', valueClass: 'score-mid' };
                      return { stroke: '#52c41a', valueClass: 'score-high' };
                    };
                    const scoreColor = getScoreColor(score);
                    return (
                      <div className="isin-detail-scan-circle-wrap">
                        <Progress
                          type="circle"
                          percent={score}
                          format={(percent) => (
                            <span className={`isin-detail-scan-circle-value ${scoreColor.valueClass}`}>
                              {percent != null ? percent.toFixed(1) : 'N/A'}
                            </span>
                          )}
                          strokeColor={scoreColor.stroke}
                          trailColor="rgba(14, 15, 12, 0.12)"
                          size={160}
                          strokeWidth={10}
                          className="isin-detail-scan-progress"
                        />
                        <div className="isin-detail-scan-circle-label">Total Score</div>
                        <div className="isin-detail-scan-circle-hint">Comprehensive risk assessment</div>
                      </div>
                    );
                  })() : (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#868685' }}>
                      <Text type="secondary">No scan score data available</Text>
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card
                  className="isin-detail-section-card modern-section-card"
                  title={
                    <div className="isin-detail-card-title">
                      <span className="isin-detail-card-title-text">
                        <TrophyOutlined className="isin-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Credit Ratings</Title>
                        {creditRatings.length > 0 && (
                          <Tag color="orange" style={{ marginLeft: '8px' }}>{creditRatings.length}</Tag>
                        )}
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openModal('ratings')}
                      >
                        View Details
                      </Button>
                    </div>
                  }
                >
                  {ratingsLoading ? (
                    <div className="isin-detail-ratings-loading">
                      <Spin size="large" />
                    </div>
                  ) : creditRatings.length > 0 ? (
                    <div className="isin-detail-ratings-list">
                      {ratingTypes.length > 1 && (
                        <Tabs
                          activeKey={activeRatingTypeTab}
                          onChange={setActiveRatingTypeTab}
                          size="small"
                          className="isin-detail-ratings-type-tabs"
                          items={ratingTypes.map((ratingType) => ({
                            key: ratingType,
                            label: (
                              <span>
                                {formatRatingTypeLabel(ratingType)}
                                <span className="isin-detail-ratings-tab-count">
                                  ({ratingsByType[ratingType].length})
                                </span>
                              </span>
                            ),
                          }))}
                        />
                      )}
                      <div className="isin-detail-ratings-header">
                        <span className="isin-detail-ratings-header-rating">Rating</span>
                        <span className="isin-detail-ratings-header-agency">Agency</span>
                      </div>
                      {previewRatings.slice(0, 4).map((rating, index) => {
                        const agencyName = rating.creditRatingAgencyName || rating.agencyName || 'N/A';
                        const currentRating = rating.currentRating || rating.rating || 'N/A';
                        const cleanedRating = currentRating.replace(/[^A-Z0-9+\-]/gi, '').toUpperCase() || '—';
                        return (
                          <div key={getRatingRowKey(rating, index)} className="isin-detail-rating-row">
                            <span className="isin-detail-rating-value">{cleanedRating}</span>
                            <span className="isin-detail-rating-agency">{agencyName}</span>
                          </div>
                        );
                      })}
                      {previewRatings.length > 4 && (
                        <div className="isin-detail-ratings-more">
                          +{previewRatings.length - 4} more ratings — see View Details
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="isin-detail-ratings-empty">
                      <Text type="secondary">No credit ratings found</Text>
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={24}>
                <Card
                  className="isin-detail-section-card modern-section-card call-put-options-card"
                  title={
                    <div className="isin-detail-card-title">
                      <span className="isin-detail-card-title-text">
                        <InfoCircleOutlined className="isin-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Call &amp; Put Options</Title>
                      </span>
                      {canExpandCallPutDetails && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setShowCallPutDetails((prev) => !prev)}
                        >
                          {showCallPutDetails ? 'Show less' : 'See more'}
                        </Button>
                      )}
                    </div>
                  }
                >
                  {optionsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <>
                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                          <div className="option-summary-item">
                            <Text type="secondary">Call Option</Text>
                            <div>
                              <Tag color="blue">
                                {(bondData.callOption || 'No').toUpperCase()}
                              </Tag>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <div className="option-summary-item">
                            <Text type="secondary">Put Option</Text>
                            <div>
                              <Tag color="blue">
                                {(bondData.putOption || 'No').toUpperCase()}
                              </Tag>
                            </div>
                          </div>
                        </Col>
                        <Col xs={24} md={8}>
                          <div className="option-summary-item">
                            <Text type="secondary">Redemption / Maturity</Text>
                            <div>
                              <Text strong>
                                {bondData.redemption || bondData.dateOfRedemption || 'N/A'}
                              </Text>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      {optionsError && !hasCallPutSummary && (
                        <div style={{ textAlign: 'center', padding: '1.5rem 0 0', color: '#64748b' }}>
                          <Text type="secondary">{optionsError}</Text>
                        </div>
                      )}
                      {showCallPutDetails && canExpandCallPutDetails && (
                        <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(100, 116, 139, 0.18)' }}>
                          <CallPutOptionsDetails data={optionsData} />
                        </div>
                      )}
                    </>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={24}>
                <Card
                  className="isin-detail-section-card modern-section-card isin-detail-cashflow-card"
                  title={
                    <div className="isin-detail-card-title">
                      <span className="isin-detail-card-title-text">
                        <DollarOutlined className="isin-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Cash Flow</Title>
                      </span>
                      {cashFlowEntries.length > cashFlowPreviewCount && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setShowAllCashFlowDetails((prev) => !prev)}
                        >
                          {showAllCashFlowDetails ? "Show less" : "See more"}
                        </Button>
                      )}
                    </div>
                  }
                >
                  {cashFlowLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : cashFlowError ? (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">{cashFlowError}</Text>
                    </div>
                  ) : cashFlowEntries.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">No cash flow data available.</Text>
                    </div>
                  ) : (
                    <Table
                      columns={cashFlowColumns}
                      dataSource={displayedCashFlowEntries.map((cf, idx) => ({
                        key: `cf-${idx}`,
                        ...cf,
                      }))}
                      pagination={false}
                      scroll={{ x: true }}
                      size="small"
                    />
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={24}>
                <Card
                  className="isin-detail-section-card modern-section-card"
                  title={
                    <div className="isin-detail-card-title">
                      <span className="isin-detail-card-title-text">
                        <FileTextOutlined className="isin-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Termsheet Details</Title>
                      </span>
                      {termsheetEntries.length > termsheetPreviewCount && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setShowAllTermsheetDetails((prev) => !prev)}
                        >
                          {showAllTermsheetDetails ? "Show less" : "See more"}
                        </Button>
                      )}
                    </div>
                  }
                >
                  {termsheetLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : termsheetData ? (
                    <Descriptions
                      bordered
                      column={1}
                      size="small"
                      labelStyle={{
                        fontWeight: 600,
                        width: 280,
                        background: isDark ? '#1f1f2b' : '#f8fafc',
                        color: isDark ? '#ffffff' : '#1a1a1a'
                      }}
                      contentStyle={{
                        wordBreak: "break-word",
                        background: isDark ? '#14141f' : '#ffffff',
                        color: isDark ? '#ffffff' : '#1a1a1a'
                      }}
                    >
                      {displayedTermsheetEntries.map(([key, value]) => (
                        <Descriptions.Item key={key} label={formatTermsheetLabel(key)}>
                          {formatTermsheetValue(value)}
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">{termsheetError || "No termsheet details available"}</Text>
                    </div>
                  )}
                </Card>
              </Col>

              <Col xs={24} lg={24}>
                <Card
                  className="isin-detail-section-card modern-section-card"
                  title={
                    <div className="isin-detail-card-title">
                      <span className="isin-detail-card-title-text">
                        <FileTextOutlined className="isin-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Documents</Title>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openModal('documents')}
                      >
                        View Details
                      </Button>
                    </div>
                  }
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div
                      className={`isin-detail-doc-item ${downloadingPDF ? 'loading' : ''}`}
                      onClick={handleDownloadPDF}
                    >
                      {downloadingPDF ? <Spin size="small" /> : <DownloadOutlined className="isin-detail-doc-icon" />}
                      <Text strong>Brief Summary Document</Text>
                    </div>
                    <div
                      className={`isin-detail-doc-item ${downloadingCreditNote ? 'loading' : ''}`}
                      onClick={handleDownloadCreditNote}
                    >
                      {downloadingCreditNote ? <Spin size="small" /> : <DownloadOutlined className="isin-detail-doc-icon" />}
                      <Text strong>Credit Note Document</Text>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Modals for View More */}
            <Modal
              title="Fundamental Details"
              open={modalVisible.fundamentalDetails}
              onCancel={() => closeModal('fundamentalDetails')}
              footer={null}
              width={1200}
            >
              <div className="fundamental-details-section">
                <Row gutter={[24, 24]}>
                  <Col xs={24} md={12}>
                    <Card className="fundamental-details-card">
                      <div className="fundamental-column">
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            ISIN
                          </div>
                          <div className="fundamental-value">
                            <Tag color="blue" className="fundamental-tag">
                              {formatValueOrDash(bondData.isin)}
                            </Tag>
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Amount raised
                          </div>
                          <div className="fundamental-value">
                            {bondData.amountRaised
                              ? bondData.issueSize < bondData.amountRaised
                                ? `${(bondData.amountRaised / 10000000).toFixed(2)} Cr`
                                : `${bondData.amountRaised} Cr`
                              : "-"}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Issue size
                          </div>
                          <div className="fundamental-value">
                            {bondData.issueSize
                              ? bondData.issueSize + " Cr"
                              : "-"}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Issue price
                          </div>
                          <div className="fundamental-value">
                            {bondData.issuePrice
                              ? "₹" + bondData.issuePrice
                              : "-"}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Coupon rate
                          </div>
                          <div className="fundamental-value">
                            {formatCouponRate(bondData.couponRate) === 'N/A' ? '-' : formatCouponRate(bondData.couponRate)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Coupon Type
                          </div>
                          <div className="fundamental-value">
                            <Tag color="green" className="fundamental-tag">
                              {bondData.couponType}
                            </Tag>
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Redemption date
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.redemption)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Coupon basis
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.couponBasis)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Redemption Type
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.redemptionType)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Put Option
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.putOption)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Call Option
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.callOption)}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>

                  <Col xs={24} md={12}>
                    <Card className="fundamental-details-card">
                      <div className="fundamental-column">
                      <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Corporate Identity Number
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.corporateIdentityNumber)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Legal Entity Identifier
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.legalEntityIdentifier)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Listed On
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.stockExchange)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Date of issue
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.allotmentDate)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Listing Date
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.listingDate)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Seniority
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.seniorityInRepayment)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Type of Issuer
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.typeOfIssuerBasedOnOwnership)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Instrument type
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.typeOfSecurity)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Security name
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.nameOfTheInstrument)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Mode of issue
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.modeOfIssue)}
                          </div>
                        </div>
                        <div className="fundamental-item">
                          <div className="fundamental-label">
                            <InfoCircleOutlined className="info-icon" />
                            Nature of instrument
                          </div>
                          <div className="fundamental-value">
                            {formatValueOrDash(bondData.typeOfInstrument)}
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Modal>

            <Modal
              title="Scan Score Analysis"
              open={modalVisible.scanScore}
              onCancel={() => closeModal('scanScore')}
              footer={null}
              width={1200}
            >
              <div className="scan-score-section">
                <Card className="scan-score-card">
                  {scanLoading ? (
                    <div style={{ textAlign: "center", padding: "2rem" }}>
                      <Spin size="large" />
                      <div style={{ marginTop: "1rem", color: isDark ? "#ffffff" : "#000000" }}>
                        Loading scan score data...
                      </div>
                    </div>
                  ) : scanData ? (
                    <Row gutter={[24, 24]}>
                      <Col xs={24} md={8}>
                        <div className="scan-score-overview">
                          <div className="scan-score-main">
                            <div className="scan-score-value">
                              <Text
                                style={{
                                  fontSize: "72px",
                                  fontWeight: "bold",
                                  color: isDark ? "#8b5cf6" : "#1890ff",
                                }}
                              >
                                {scanData.totalWeightedScore?.toFixed(1) || "N/A"}
                              </Text>
                            </div>
                            <div className="scan-score-label">
                              <Text style={{ fontSize: "25px", color: isDark ? "rgba(255, 255, 255, 0.8)" : "#666", fontWeight: "bolder" }}>
                                Total Score
                              </Text>
                            </div>
                          </div>
                          <div className="scan-score-description">
                            <Text type="secondary">
                              Comprehensive risk assessment based on financial
                              strength, governance, market activity, and
                              complexity factors.
                            </Text>
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} md={16}>
                        <div className="scan-score-chart-container">
                          <ScanScoreChart scanData={scanData} isDark={isDark} />
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "2rem",
                        color: isDark ? "rgba(255, 255, 255, 0.8)" : "#64748b",
                      }}
                    >
                      <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                        📊
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "500",
                          marginBottom: "0.5rem",
                          color: isDark ? "#ffffff" : "#000000",
                        }}
                      >
                        No Scan Score Data Available
                      </div>
                      <div style={{ fontSize: "14px", color: isDark ? "rgba(255, 255, 255, 0.8)" : "#64748b" }}>
                        Scan score analysis is not available for this ISIN at the
                        moment.
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            </Modal>

            <Modal
              title="Credit Ratings"
              open={modalVisible.ratings}
              onCancel={() => closeModal('ratings')}
              footer={null}
              width={1200}
            >
              <CreditRatingsTable isin={bondData?.isin || isin} />
            </Modal>

            <Modal
              title="Documents"
              open={modalVisible.documents}
              onCancel={() => closeModal('documents')}
              footer={null}
              width={800}
            >
              <div className="documents-section">
                <Title level={4} className="documents-title">
                  Related documents
                </Title>
                <div className="documents-container">
                  <div
                    className="document-item"
                    onClick={handleDownloadPDF}
                    style={{
                      cursor: downloadingPDF ? "not-allowed" : "pointer",
                      opacity: downloadingPDF ? 0.6 : 1,
                    }}
                  >
                    <div className="document-icon">
                      {downloadingPDF ? (
                        <Spin size="small" />
                      ) : (
                        <DownloadOutlined />
                      )}
                    </div>
                    <div className="document-text">
                      {downloadingPDF
                        ? "Generating PDF..."
                        : "Brief Summary Document"}
                    </div>
                  </div>
                  <div
                    className="document-item"
                    onClick={handleDownloadCreditNote}
                    style={{
                      cursor: downloadingCreditNote ? "not-allowed" : "pointer",
                      opacity: downloadingCreditNote ? 0.6 : 1,
                    }}
                  >
                    <div className="document-icon">
                      {downloadingCreditNote ? (
                        <Spin size="small" />
                      ) : (
                        <DownloadOutlined />
                      )}
                    </div>
                    <div className="document-text">
                      {downloadingCreditNote
                        ? "Generating PDF..."
                        : "Credit Note Document"}
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="disclaimer-section" style={{ marginTop: 24 }}>
                  <Title level={4} className="disclaimer-title">
                    Disclaimer
                  </Title>
                  <div className="disclaimer-content">
                    <div className="disclaimer-item">
                      <div className="disclaimer-icon">
                        <ExclamationCircleOutlined />
                      </div>
                      <div className="disclaimer-text">
                        This information is provided for informational purposes only and
                        should not be construed as investment advice. The data presented
                        here is sourced from publicly available information and may not
                        be complete or up-to-date. Investors should conduct their own
                        research and consult with qualified financial advisors before
                        making any investment decisions. Past performance does not
                        guarantee future results. Investment in bonds carries risks
                        including but not limited to credit risk, interest rate risk,
                        and market risk. The issuer and its affiliates are not
                        responsible for any losses that may arise from the use of this
                        information.
                      </div>
                    </div>
                    <div className="disclaimer-item">
                      <div className="disclaimer-icon">
                        <ExclamationCircleOutlined />
                      </div>
                      <div className="disclaimer-text">
                        The ratings, if any, are provided by rating agencies and are
                        subject to change. These ratings do not constitute a
                        recommendation to buy, sell, or hold any security. Investors
                        should carefully read the prospectus and other offering
                        documents before investing. All investments involve risk and the
                        value of investments may go down as well as up.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          </Col>
        </Row>
      </div>


    </div>
  );

  const pageContent = (
    <>
      {content}
      <InstrumentChatBot isin={isin} bondData={bondData} isDark={isDark} />
    </>
  );

  return skipLayout ? pageContent : <LandingLayout>{pageContent}</LandingLayout>;
};

export default InstrumentDetails;
